/**
 * Client-side error reporter
 *
 * Captures and forwards browser errors to the `client_errors` Supabase table:
 *   - console.error / console.warn
 *   - window 'error' (uncaught exceptions + resource load failures)
 *   - window 'unhandledrejection'
 *   - failed fetch() calls (network errors + non-2xx HTTP responses)
 *
 * Design constraints:
 *   - Never break the app: every report path is wrapped in try/catch and
 *     console output is preserved (we wrap, never replace silently).
 *   - Avoid feedback loops: reports themselves are not re-reported, and
 *     Supabase calls to the client_errors table are filtered out.
 *   - Throttle/dedupe: identical errors within 30s are batched, and we cap
 *     total reports per session to avoid runaway storms.
 *   - Idempotent: init() is safe to call multiple times.
 */
import { supabase } from "@/integrations/supabase/client";

type ErrorType =
  | "console_error"
  | "console_warn"
  | "window_error"
  | "unhandled_rejection"
  | "network_error"
  | "resource_error";

interface ErrorReport {
  error_type: ErrorType;
  message: string;
  source_url?: string | null;
  line_number?: number | null;
  column_number?: number | null;
  stack?: string | null;
  page_url?: string | null;
  user_agent?: string | null;
  user_id?: string | null;
  metadata?: Record<string, unknown>;
}

const MAX_REPORTS_PER_SESSION = 50;
const DEDUPE_WINDOW_MS = 30_000;
const MAX_MESSAGE_LENGTH = 2000;
const MAX_STACK_LENGTH = 4000;

let initialized = false;
let reportCount = 0;
const recentSignatures = new Map<string, number>();
let isReporting = false; // guards against feedback loops

// Patterns we deliberately ignore to keep the table signal-rich.
const IGNORE_MESSAGE_PATTERNS = [
  /ResizeObserver loop/i,
  /Non-Error promise rejection captured/i,
  /Script error\.?$/i, // cross-origin script errors with no detail
  /^Loading chunk \d+ failed/i, // user-side network blip during deploy
  /Unknown message type: RESET_BLANK_CHECK/i, // Lovable preview iframe noise
];

const truncate = (s: string | undefined | null, max: number): string | null => {
  if (!s) return null;
  return s.length > max ? s.slice(0, max) + "…[truncated]" : s;
};

const shouldIgnore = (message: string): boolean => {
  return IGNORE_MESSAGE_PATTERNS.some((re) => re.test(message));
};

const dedupeKey = (type: ErrorType, message: string): string => {
  // Truncate to first 200 chars so similar errors collapse together.
  return `${type}::${message.slice(0, 200)}`;
};

const isDuplicate = (key: string): boolean => {
  const now = Date.now();
  // Sweep stale entries opportunistically.
  if (recentSignatures.size > 100) {
    for (const [k, t] of recentSignatures) {
      if (now - t > DEDUPE_WINDOW_MS) recentSignatures.delete(k);
    }
  }
  const last = recentSignatures.get(key);
  if (last !== undefined && now - last < DEDUPE_WINDOW_MS) return true;
  recentSignatures.set(key, now);
  return false;
};

const formatArgs = (args: unknown[]): string => {
  return args
    .map((a) => {
      if (a instanceof Error) return a.stack || a.message;
      if (typeof a === "string") return a;
      try {
        return JSON.stringify(a);
      } catch {
        return String(a);
      }
    })
    .join(" ");
};

const sendReport = async (report: ErrorReport): Promise<void> => {
  if (reportCount >= MAX_REPORTS_PER_SESSION) return;
  if (shouldIgnore(report.message)) return;

  const key = dedupeKey(report.error_type, report.message);
  if (isDuplicate(key)) return;

  reportCount++;
  isReporting = true;
  try {
    const { data: userData } = await supabase.auth.getUser().catch(() => ({ data: { user: null } }));
    const payload = {
      error_type: report.error_type,
      message: truncate(report.message, MAX_MESSAGE_LENGTH) ?? "(no message)",
      source_url: truncate(report.source_url ?? null, 500),
      line_number: report.line_number ?? null,
      column_number: report.column_number ?? null,
      stack: truncate(report.stack ?? null, MAX_STACK_LENGTH),
      page_url: report.page_url ?? (typeof window !== "undefined" ? window.location.href : null),
      user_agent: report.user_agent ?? (typeof navigator !== "undefined" ? navigator.userAgent : null),
      user_id: userData?.user?.id ?? null,
      metadata: report.metadata ?? {},
    };
    await (supabase.from("client_errors") as any).insert([payload]);
  } catch {
    // Swallow — we never want the reporter to itself produce errors.
  } finally {
    isReporting = false;
  }
};

const installConsoleHooks = (): void => {
  const origError = console.error.bind(console);
  const origWarn = console.warn.bind(console);

  console.error = (...args: unknown[]) => {
    origError(...args);
    if (isReporting) return;
    void sendReport({
      error_type: "console_error",
      message: formatArgs(args),
      stack: args.find((a) => a instanceof Error)
        ? (args.find((a) => a instanceof Error) as Error).stack ?? null
        : null,
    });
  };

  console.warn = (...args: unknown[]) => {
    origWarn(...args);
    if (isReporting) return;
    void sendReport({
      error_type: "console_warn",
      message: formatArgs(args),
    });
  };
};

const installWindowHooks = (): void => {
  window.addEventListener("error", (event) => {
    if (isReporting) return;
    // Resource load failures (img/script/link) have a target and no `error` instance.
    const target = event.target as HTMLElement | null;
    if (target && target !== (window as unknown as EventTarget) && "tagName" in target) {
      const el = target as HTMLImageElement | HTMLScriptElement | HTMLLinkElement;
      const url = (el as HTMLImageElement).src || (el as HTMLLinkElement).href || "";
      void sendReport({
        error_type: "resource_error",
        message: `Failed to load ${el.tagName.toLowerCase()}: ${url}`,
        source_url: url,
        metadata: { tag: el.tagName.toLowerCase() },
      });
      return;
    }
    void sendReport({
      error_type: "window_error",
      message: event.message || "Unknown error",
      source_url: event.filename || null,
      line_number: event.lineno ?? null,
      column_number: event.colno ?? null,
      stack: event.error?.stack ?? null,
    });
  }, true); // capture phase to catch resource errors

  window.addEventListener("unhandledrejection", (event) => {
    if (isReporting) return;
    const reason = event.reason;
    let message = "Unhandled promise rejection";
    let stack: string | null = null;
    if (reason instanceof Error) {
      message = reason.message || message;
      stack = reason.stack ?? null;
    } else if (typeof reason === "string") {
      message = reason;
    } else {
      try {
        message = JSON.stringify(reason);
      } catch {
        message = String(reason);
      }
    }
    void sendReport({
      error_type: "unhandled_rejection",
      message,
      stack,
    });
  });
};

const installFetchHook = (): void => {
  const origFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    let url = "";
    try {
      url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
    } catch {
      // ignore
    }
    // Don't track requests to the client_errors table itself (avoids feedback loops).
    const isSelfReport = url.includes("/rest/v1/client_errors");
    try {
      const res = await origFetch(input, init);
      if (!isSelfReport && !res.ok && res.status >= 400) {
        void sendReport({
          error_type: "network_error",
          message: `${res.status} ${res.statusText} — ${init?.method ?? "GET"} ${url}`,
          source_url: url,
          metadata: { status: res.status, method: init?.method ?? "GET" },
        });
      }
      return res;
    } catch (err) {
      if (!isSelfReport) {
        const e = err as Error;
        void sendReport({
          error_type: "network_error",
          message: `Network failure — ${init?.method ?? "GET"} ${url}: ${e.message}`,
          source_url: url,
          stack: e.stack ?? null,
          metadata: { method: init?.method ?? "GET" },
        });
      }
      throw err;
    }
  };
};

export const initErrorReporter = (): void => {
  if (initialized) return;
  if (typeof window === "undefined") return;
  initialized = true;
  try {
    installConsoleHooks();
    installWindowHooks();
    installFetchHook();
  } catch {
    // never throw from init
  }
};
